import { call, put, select, takeLatest } from 'redux-saga/effects';
import { Produktlinienname, Selbstbeteiligung } from '@ps20/bff-models';
import {
  DoRequestResponse,
  GlobalActions,
  GlobalPut,
  HTTP_METHOD,
} from '@ps20-frontend/shop-integration-redux';
import { App } from '@ps20-frontend/frontend-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { IAppState } from '../appState';
import { VhvActions } from '../vhvActions';
import { VersicherungssummeForLinie } from '../action-types/versicherungssummenResponseReceived';
import {
  clampEntschaedigungsgrenze,
  EntschaedigungsgrenzeRadForLinie,
} from '../model/entschaedigungsgrenzeRad.model';

export const FAHRRAD_ENTSCHAEDIGUNGSGRENZE_URL =
  '/privatschutz/api/fahrradentschaedigungsgrenze';

export enum FAHRRAD_ENTSCHAEDIGUNGSGRENZE_REQUEST_EVENTS {
  REQUEST_SUCCESSFUL = 'FAHRRAD_ENTSCHAEDIGUNGSGRENZE_REQUEST_SUCCESSFUL',
}

export interface FahrradEntschaedigungsgrenzeRequestLinie {
  produktlinie: Produktlinienname;
  versicherungssumme: number;
  selbstbeteiligung: number;
}
export interface FahrradEntschaedigungsgrenzeRequestModel {
  linien: FahrradEntschaedigungsgrenzeRequestLinie[];
}
export interface FahrradEntschaedigungsgrenzeResponseLinie {
  produktline: Produktlinienname;
  versicherbar: boolean;
  defaultwert: number | null;
  minInput: number | null;
  maxInput: number | null;
  schrittweite: number | null;
}
export interface FahrradEntschaedigungsgrenzeResponseModel {
  entschaedigungsgrenzen: FahrradEntschaedigungsgrenzeResponseLinie[];
}

export const selbstbeteiligungToBetrag = (
  selbstbeteiligung: Selbstbeteiligung,
): number | null => {
  return {
    [Selbstbeteiligung.SELBSTBETEILIGUNG_0000]: 0,
    [Selbstbeteiligung.SELBSTBETEILIGUNG_0150]: 150,
    [Selbstbeteiligung.SELBSTBETEILIGUNG_0300]: 300,
    [Selbstbeteiligung.SELBSTBETEILIGUNG_0500]: 500,
    [Selbstbeteiligung.SELBSTBETEILIGUNG_1000]: 1000,
    [Selbstbeteiligung.SELBSTBETEILIGUNG_2000]: 2000,
    [Selbstbeteiligung.SELBSTBETEILIGUNG_5000]: 5000,
    [Selbstbeteiligung.SELBSTBETEILIGUNG_10000]: 10000,
    [Selbstbeteiligung.SELBSTBETEILIGUNG_10_PROZENT]: null,
    [Selbstbeteiligung.SELBSTBETEILIGUNG_20_PROZENT]: null,
    [Selbstbeteiligung.SELBSTBETEILIGUNG_30_PROZENT]: null,
    [Selbstbeteiligung.SELBSTBETEILIGUNG_40_PROZENT]: null,
  }[selbstbeteiligung];
};

export function* watchFahrradEntschaedigungsgrenzeTrigger() {
  yield takeLatest(
    [VhvActions.VERSICHERUNGSSUMMEN_RESPONSE_RECEIVED, VhvActions.VERSICHERUNGSSUMME_CHANGED],
    safeSaga(requestFahrradEntschaedigungsgrenzeFromState, App.VHV_V2),
  );
  yield takeLatest(
    VhvActions.SELBSTBETEILIGUNG_CHANGED,
    safeSaga(requestFahrradEntschaedigungsgrenzeFromState, App.VHV_V2),
  );
  yield takeLatest(
    FAHRRAD_ENTSCHAEDIGUNGSGRENZE_REQUEST_EVENTS.REQUEST_SUCCESSFUL,
    safeSaga(fahrradEntschaedigungsgrenzeRequestSuccessful, App.VHV_V2),
  );
}

export function* requestFahrradEntschaedigungsgrenzeFromState() {
  const state: IAppState = yield select();
  yield requestFahrradEntschaedigungsgrenze(state.versicherungssummen);
}

export function* requestFahrradEntschaedigungsgrenze(
  versicherungssummen: VersicherungssummeForLinie[],
) {
  const state: IAppState = yield select();
  const selbstbeteiligung = selbstbeteiligungToBetrag(
    state.vertragsoptionen?.selbstbeteiligung,
  );
  if (selbstbeteiligung == null) {
    return;
  }

  const linien: FahrradEntschaedigungsgrenzeRequestLinie[] = (versicherungssummen ?? []).map(
    summe => ({
      produktlinie: summe.produktlinie,
      versicherungssumme: summe.summe,
      selbstbeteiligung,
    }),
  );
  if (linien.length === 0) {
    return;
  }

  const requestModel: FahrradEntschaedigungsgrenzeRequestModel = { linien };
  const globalPut = GlobalPut.getInstance();
  yield call(
    [globalPut, globalPut.put],
    new GlobalActions().doRequest({
      appId: App.VHV_V2,
      angebotsId: state.angebotsId,
      method: HTTP_METHOD.POST,
      body: requestModel,
      url: FAHRRAD_ENTSCHAEDIGUNGSGRENZE_URL,
      successEvent: FAHRRAD_ENTSCHAEDIGUNGSGRENZE_REQUEST_EVENTS.REQUEST_SUCCESSFUL,
      errorEvent: null,
    }),
  );
}

export function* fahrradEntschaedigungsgrenzeRequestSuccessful(
  action: DoRequestResponse<FahrradEntschaedigungsgrenzeResponseModel>,
) {
  const state: IAppState = yield select();
  if (action.doRequestAction.payload.angebotsId !== state.angebotsId) {
    return;
  }

  const bestehendeEntschaedigungsgrenzen = state.entschaedigungsgrenzeRad ?? [];

  const entschaedigungsgrenzen: EntschaedigungsgrenzeRadForLinie[] =
    action.payload.entschaedigungsgrenzen.map(linie => {
      const bestehend = bestehendeEntschaedigungsgrenzen.find(
        bestehende => bestehende.produktlinie === linie.produktline,
      );
      const betrag =
        linie.versicherbar && bestehend?.betrag != null
          ? clampEntschaedigungsgrenze(bestehend.betrag, linie.minInput, linie.maxInput)
          : linie.defaultwert;

      return {
        produktlinie: linie.produktline,
        betrag,
        defaultwert: linie.defaultwert,
        minInput: linie.minInput,
        maxInput: linie.maxInput,
        schrittweite: linie.schrittweite,
        available: linie.versicherbar,
      };
    });

  yield put(new VhvActions().entschaedigungsgrenzeRadReceived(entschaedigungsgrenzen));
}
